import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmsCampaignShortcuts {
  id?: number;
  sms_campaign_id?: number;
  code: string;
  message: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SmsCampaignListState {
  smscampaignshortcutsdata: SmsCampaignShortcuts[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

// ====================-SmsCampgain-listing-action-start==============
export const GetallSmsCampaignShortcuts = createAsyncThunk<
  {
    data: SmsCampaignShortcuts[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number; sms_campaign_id: number },
  { rejectValue: string }
>(
  "GetallSmsCampaignShortcuts",
  async ({ search, page, limit, sms_campaign_id }, { rejectWithValue }) => {
    try {
      await delay(1000)
      const response = await axiosInstance.get(
        `api/admin/sms-campaign/shortcut-message`,
        {
          params: { search, page, limit, sms_campaign_id },
        }
      );
      return { data: response.data.data, info: response.data.info };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// ====================SmsCampgain-listing-action-start==============

// ==================AddSmsCampaigns-action-start===========
export const AddSmsCampaignsShortcuts = createAsyncThunk<
  SmsCampaignShortcuts,
  SmsCampaignShortcuts
>(
  "smscampaignshortcutsdata/AddSmsCampaignsShortcuts",
  async (SmsCampaignShortcutsData) => {
    try {
      const { data } = await axiosInstance.post(
        `api/admin/sms-campaign/shortcut-message/create`,
        SmsCampaignShortcutsData
      );
      return data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      return error.message;
    }
  }
);
// ==================AddSmsCampaigns-action-end===========

// =================Update-smscampaign-status-action-start=============
export const UpdateSmsCampaignsShortcuts = createAsyncThunk<
  SmsCampaignShortcuts,
  { id: number; updateData: SmsCampaignShortcuts }
>(
  "smscampaignshortcutsdata/UpdateSmsCampaignsShortcuts",
  async ({ id, updateData }) => {
    try {
      const response = await axiosInstance.put(
        `api/admin/sms-campaign/shortcut-message/update/${id}`,
        updateData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      return error.message;
    }
  }
);
// =================Update-smscampaign-status-action-end=============

// ================DeleteSmsCampaign-action-start=========
export const DeleteSmsCampaignShortcuts = createAsyncThunk<
  { id: number },
  { id: number }
>("smscampaignshortcutsdata/DeleteSmsCampaignShortcuts", async ({ id }) => {
  try {
    const { data } = await axiosInstance.delete(
      `api/admin/sms-campaign/shortcut-message/${id}`
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return error.message;
  }
});
// ================DeleteSmsCampaign-action-end=========

const initialState: SmsCampaignListState = {
  smscampaignshortcutsdata: [],
  info: null,
  error: null,
  loading: false,
};

const smscampaignshortcutsSlice = createSlice({
  name: "Smscampaignshortcuts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-sms-campaign-shortcuts-slice-start============
      .addCase(GetallSmsCampaignShortcuts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallSmsCampaignShortcuts.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: SmsCampaignShortcuts[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.smscampaignshortcutsdata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallSmsCampaignShortcuts.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-sms-campaign-shortcuts-slice-end============

    // =================add-=sms-campaign-shortcuts-slice-start============
    builder
      .addCase(AddSmsCampaignsShortcuts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmsCampaignsShortcuts.fulfilled,
        (state, action: PayloadAction<SmsCampaignShortcuts>) => {
          state.loading = false;
          state.smscampaignshortcutsdata.push(action.payload);
        }
      )
      .addCase(AddSmsCampaignsShortcuts.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to add sms campaign shortcuts";
      });
    // =================add-=sms-campaign-shortcuts-slice-end============

    // =================Update-sms-campaign-shortcuts-slice-start============
    builder
      .addCase(UpdateSmsCampaignsShortcuts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmsCampaignsShortcuts.fulfilled,
        (state, action: PayloadAction<SmsCampaignShortcuts>) => {
          state.loading = false;
          const index = state.smscampaignshortcutsdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smscampaignshortcutsdata[index] = action.payload;
          }
        }
      )
      .addCase(UpdateSmsCampaignsShortcuts.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to update smscampaignshortcuts";
      });
    // =================Update-sms-campaign-shortcuts-slice-end============

    // =================Delete-smtp-slice-start============
    builder
      .addCase(DeleteSmsCampaignShortcuts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        DeleteSmsCampaignShortcuts.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
          state.smscampaignshortcutsdata =
            state.smscampaignshortcutsdata.filter(
              (item) => item.id !== action.payload.id
            );
        }
      )
      .addCase(DeleteSmsCampaignShortcuts.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete smscampaignshortcuts";
      });
    // =================Delete-smtp-slice-end============
  },
});

export default smscampaignshortcutsSlice.reducer;
